/**
 * Panic (SD-6, STAGE-DESIGN §2) — the stage kill switch.
 *
 * v0 (zero OPBridge dependency): fire CC 120/121/123 across all 16 channels to
 * EVERY paired bridge's own default MIDI output (All Sound Off / Reset
 * Controllers / All Notes Off — matching OPBridge's own admin `/midi/panic`),
 * AND stop every locally-known armed/running engine on every bridge. Reports
 * what landed via a toast.
 *
 * v1 (WIRED 2026-07-28, §6.2): additionally POST /midi/panic +
 * /engines/opbuttons/stopAll, concurrently with the v0 path. Verified live
 * on `v1.15.24+7dbd561` before wiring — the Session-138 batch had in fact
 * been beta-released for eleven days while the cross-project notepad still
 * said "NOT beta-released", so this stayed on v0 long after it needed to.
 *
 * BOTH paths fire, always. v1 does not supersede v0 — see `panicNative` in
 * dispatch.ts for the two independent reasons (reach, and peer age). The
 * cost of the redundancy is a handful of extra HTTP calls on the one
 * control where being slightly wasteful is unambiguously correct.
 *
 * EVERY LEG FANS OUT TO EVERY BRIDGE (2026-09-03). All three used to default
 * to target `''`, which `resolveBridges` resolves to the single default
 * bridge. On the operator's real two-machine rig that meant PANIC silenced one
 * and left the other sounding with its LFOs running — and the toast still read
 * `ok`, because the summary only inspected the first result. Measured, not
 * inferred: `panic_reaches_every_bridge.ts` recorded 0 of 48 messages reaching
 * the second bridge before the fix. Full account in
 * `audits/reports/2026-09-03-live-risk.md` finding 1.
 *
 * Instant + unguarded by design (SD-6): panic latency beats accidental-tap
 * cost — an accidental panic is fully recoverable (notes off, engines
 * stopped, nothing destroyed), a delayed real emergency is not. The hold-to-
 * latch gesture below is strictly additive and cannot delay a tap.
 */

import { sendMidiPanic, stopAllEngines, panicNative, panicOpVisual } from '../opbridge/dispatch.ts';
import { opVisualPanicAims } from '../opvisual/machine.ts';
import { pushToast, dismissToast, type ToastKind } from '../ui/toast.ts';
import { stopAllPads } from '../audio/pads.ts';
import { panicLatched, setPanicLatched, LATCH_REPEAT_MS } from './panicLatch.ts';

// One panic in flight is enough — coalesce rapid double-taps so a second
// overlapping storm doesn't stack 96 sends + a duplicate toast.
let inFlight = false;

/**
 * Run a panic. Fires the MIDI storm + engine stop concurrently across every
 * paired bridge, then surfaces one summary toast. Never throws.
 *
 * `quiet` suppresses the toast — used by the latch's repeat, which would
 * otherwise stack a new toast every couple of seconds and bury the LOCKED
 * banner the operator actually needs to read.
 */
export async function panic(opts: { quiet?: boolean } = {}): Promise<void> {
  if (inFlight) return;
  inFlight = true;
  // Audio pads (2026-10-13) stop too, alongside the legs below and outside their summary: a pad still sounding after
  // PANIC is exactly what the operator pressed it to end, and an OPBridge without pads answers nothing to report.
  void stopAllPads();
  try {
    // The fourth leg (2026-10-06, OPVisual's ask): OPVisual's own panic on
    // every machine this project's OPVisual items send to. Before it, the main
    // PANIC left OPVisual on air while the operator believed it cleared it.
    const [midiResults, engineResults, nativeResults, opvResults] = await Promise.all([
      sendMidiPanic(),
      stopAllEngines(),
      panicNative(),
      panicOpVisual(opVisualPanicAims()),
    ]);
    if (!opts.quiet) {
      const { kind, message } = summarize(midiResults, engineResults, nativeResults, opvResults);
      // A CLEAN panic self-dismisses like any other toast. A PARTIAL or FAILED
      // one is sticky (ms = 0), because it is the only record that exists:
      // toasts dwell 5-6s, and the traffic monitor's rings only accumulate
      // while the Settings panel is open, which it is not during a set
      // (trafficMonitor.ts). An operator who taps PANIC and then looks at the
      // STAGE rather than the iPad had no way to learn afterwards that half of
      // it failed. Live-risk audit 2026-09-03, finding 6.
      pushToast(kind, message, kind === 'ok' ? undefined : 0);
    }
  } finally {
    inFlight = false;
  }
}

// ── The latch ───────────────────────────────────────────────────────────────
//
// Semantics, mechanism and the known boundary all live in `panicLatch.ts`.
// This file owns only the repeat timer and the sticky toast, because both are
// panic-shaped rather than state-shaped.

let repeatTimer: ReturnType<typeof setInterval> | null = null;
let latchToastId: number | null = null;

/** Engage the latch: rig stays dead until released. Idempotent. */
export function engagePanicLatch(): void {
  if (panicLatched()) return;
  setPanicLatched(true);
  void panic({ quiet: true });
  repeatTimer = setInterval(() => { void panic({ quiet: true }); }, LATCH_REPEAT_MS);
  // ms = 0 makes it sticky. A self-dismissing toast would be actively harmful
  // here: the app is deliberately not sending anything, and an operator who
  // missed a 5-second toast would be left tapping dead controls with no
  // explanation on screen (DEC-138 — never a silent dead-tap).
  latchToastId = pushToast('warn', 'PANIC LOCKED - rig silenced, all sending blocked. Press PANIC to release.', 0);
}

/** Release the latch. Never guarded by a confirmation: getting OUT must not be
 *  harder than getting in. */
export function releasePanicLatch(): void {
  if (!panicLatched()) return;
  setPanicLatched(false);
  if (repeatTimer !== null) { clearInterval(repeatTimer); repeatTimer = null; }
  if (latchToastId !== null) { dismissToast(latchToastId); latchToastId = null; }
  pushToast('ok', 'Panic released - sending resumed.');
}

export { panicLatched };

// ── Reporting ───────────────────────────────────────────────────────────────

function summarize(
  midi: Awaited<ReturnType<typeof sendMidiPanic>>,
  engines: Awaited<ReturnType<typeof stopAllEngines>>,
  native: Awaited<ReturnType<typeof panicNative>>,
  opv: Awaited<ReturnType<typeof panicOpVisual>> = [],
): { kind: ToastKind; message: string } {
  const r = summarizeAudio(midi, engines, native);
  // OPVisual is its own clause, so a PANIC that silenced the audio but left
  // OPVisual on air never reads clean. Nothing aimed = nothing said.
  const cleared = opv.filter((o) => o.kind === 'ok');
  const missed = opv.filter((o) => o.kind !== 'ok');
  let tail = '';
  if (cleared.length > 0) {
    const how = cleared.every((o) => o.via === 'osc') ? ' (OSC, unconfirmed)' : '';
    tail += ` · OPVisual cleared on ${cleared.map((o) => o.name).join(', ')}${how}`;
  }
  if (missed.length > 0) {
    tail += ` · OPVisual NOT cleared on ${missed.map((o) => `${o.name}: ${opvReason(o)}`).join(', ')}`;
  }
  const kind: ToastKind = missed.length > 0 && r.kind === 'ok' ? 'warn' : r.kind;
  return { kind, message: r.message + tail };
}

function opvReason(o: Awaited<ReturnType<typeof panicOpVisual>>[number]): string {
  switch (o.kind) {
    case 'app-not-linked':      return 'OPVisual is not linked to OPBridge there';
    case 'bridge-disconnected': return 'disconnected';
    case 'bridge-unknown':
    case 'no-bridges':          return 'that computer is not paired';
    default:                    return o.detail ?? o.kind;
  }
}

function summarizeAudio(
  midi: Awaited<ReturnType<typeof sendMidiPanic>>,
  engines: Awaited<ReturnType<typeof stopAllEngines>>,
  native: Awaited<ReturnType<typeof panicNative>>,
): { kind: ToastKind; message: string } {
  // The two paths race to stop the same engines, so whichever wins reports
  // the kill and the other reports 0. max() is the honest "at least this
  // many" — summing would double-count a single stopped LFO as two.
  const enginesStopped = engines.reduce((n, e) => n + e.stopped.length, 0);
  const nativeStopped = native.reduce((n, r) => n + (r.enginesStopped ?? 0), 0);
  const stoppedCount = Math.max(enginesStopped, nativeStopped);
  const enginesTail =
    stoppedCount > 0 ? ` · stopped ${stoppedCount} engine${stoppedCount === 1 ? '' : 's'}` : '';

  const noBridges = midi.length === 0 || midi.every((m) => m.kind === 'no-bridges');
  if (noBridges) {
    return { kind: 'error', message: 'PANIC: no OPBridge connected - nothing sent' };
  }

  // Per-bridge, not blended. A single number across a multi-machine rig is
  // exactly what hid the single-bridge bug for months: "48 msgs sent" was true
  // and useless, because it never said 48 to HOW MANY of the machines.
  const landed = midi.filter((m) => m.kind === 'ok');
  const failed = midi.filter((m) => m.kind !== 'ok');
  const totalSent = landed.reduce((n, m) => n + (m.sent ?? 0), 0);
  const portsHit = native.reduce((n, r) => n + (r.portsHit ?? 0), 0);
  const nativeTail = portsHit > 0 ? ` · global panic → ${portsHit} ports` : '';

  // Nothing landed anywhere: the only genuine total failure.
  if (landed.length === 0) {
    return {
      kind: 'error',
      message: `PANIC FAILED on all ${failed.length} bridge${failed.length === 1 ? '' : 's'} - ${describeFailures(failed)}` + enginesTail,
    };
  }

  const bridgeTail = `${landed.length}/${midi.length} bridge${midi.length === 1 ? '' : 's'}`;

  // At least one bridge landed. Warn rather than claim success if ANY other
  // bridge failed — a half-panic on stage is something the operator needs to
  // know about, not something to smooth over.
  if (failed.length > 0) {
    return {
      kind: 'warn',
      message: `PANIC PARTIAL - ${bridgeTail}, ${totalSent} MIDI msgs${nativeTail}${enginesTail} · FAILED: ${describeFailures(failed)}`,
    };
  }

  const engineErrors = engines.some((e) => e.errors.length > 0) || native.some((r) => r.errors.length > 0);
  return {
    kind: engineErrors ? 'warn' : 'ok',
    message: `PANIC sent - ${bridgeTail}, ${totalSent} MIDI msgs${nativeTail}${enginesTail}`,
  };
}

/** Name the machine AND the reason. A panic message that says only "failed"
 *  sends the operator to check everything; one that names the bridge sends
 *  them to the right machine. */
function describeFailures(failed: Awaited<ReturnType<typeof sendMidiPanic>>): string {
  return failed.map((m) => `${m.name ?? m.target}: ${reasonOf(m)}`).join(', ');
}

function reasonOf(m: Awaited<ReturnType<typeof sendMidiPanic>>[number]): string {
  switch (m.kind) {
    // Deliberately does NOT name a port. It used to hardcode "OPButtons",
    // which became a lie the moment the default moved and a per-bridge
    // override existed - and naming the wrong port in a panic message is
    // worse than naming none, because it sends the operator to check the
    // wrong thing mid-set.
    case 'port-not-configured':  return 'no MIDI output port set (Settings > Connect)';
    case 'bridge-disconnected':  return 'disconnected';
    case 'bridge-unknown':       return 'unknown bridge';
    case 'no-bridges':           return 'not paired';
    case 'http-error':           return `send failed (${m.detail ?? 'error'})`;
    default:                     return m.kind;
  }
}
